<template>
  <div class="fill-height">
    <TabBasic :actual-breadcrumb="actualBreadcrumb" show-breadcrumbs>
      <template v-slot:header>
        <v-spacer/>
        <div class="d-flex">
          <div class="mr-3">
            <v-select-custom
                ref="customFilter"
                color="white"
                class="select-filter interval-picker"
                dense
                :style="{'--accentText-color': $vuetify.theme.currentTheme.accentText}"
                v-model="customFilter"
                :items="customFilters"
                :label="$t('user.interval')"
                hide-details
                return-object
                clearable
                @openMenu="openMenu"
                @change="changeCustomFilter">
            </v-select-custom>
          </div>
          <div class="mr-3">
            <DatetimePicker
                ref="startDate"
                v-model="startDate"
                :date-picker-props="{color: 'accent accentText--text', allowedDates: allowedStartDates}"
                :label="$t('startDate')"
                :text-field-props="{hideDetails: true}"
                :time-picker-props="{color: 'accent accentText--text', format: '24hr', max: maxTime }"
                event-color="accent"
                header-filter
                @openMenu="openMenu"
                @updateDate="updateStartDate">
              <template v-slot:dateIcon>
                <v-icon>
                  mdi-calendar
                </v-icon>
              </template>
              <template v-slot:timeIcon>
                <v-icon>
                  mdi-clock
                </v-icon>
              </template>
              <template v-slot:actions="{parent}">
                <v-btn v-if="parent.activeTab === 0" :disabled="!showStartToday"
                       color="primary darken-1" text
                       @click="parent.today">{{ $t('today') }}
                </v-btn>
                <template v-if="parent.activeTab === 1">
                  <v-btn :disabled="!showStartNow" color="primary darken-1" text @click="parent.now">
                    {{
                      $t('now')
                    }}
                  </v-btn>
                  <v-btn color="primary darken-1" text @click="parent.okHandler">{{
                      $t('ok')
                    }}
                  </v-btn>
                </template>
              </template>
            </DatetimePicker>
          </div>
          <div class="mr-3">
            <DatetimePicker
                ref="endDate"
                v-model="endDate"
                :date-picker-props="{color: 'accent accentText--text', allowedDates: allowedEndDates}"
                :label="$t('endDate')"
                :text-field-props="{hideDetails: true}"
                :time-picker-props="{color: 'accent accentText--text', format: '24hr', min: minTime}"
                event-color="secondary"
                header-filter
                @openMenu="openMenu"
                @updateDate="updateEndDate">
              <template v-slot:dateIcon>
                <v-icon>
                  mdi-calendar
                </v-icon>
              </template>
              <template v-slot:timeIcon>
                <v-icon>
                  mdi-clock
                </v-icon>
              </template>
              <template v-slot:actions="{parent}">
                <v-btn v-if="parent.activeTab === 0" :disabled="!showEndToday" color="primary darken-1"
                       text
                       @click="parent.today">{{ $t('today') }}
                </v-btn>
                <template v-if="parent.activeTab === 1">
                  <v-btn :disabled="!showEndNow" color="primary darken-1" text @click="parent.now">{{
                      $t('now')
                    }}
                  </v-btn>
                  <v-btn color="primary darken-1" text @click="parent.okHandler">{{
                      $t('ok')
                    }}
                  </v-btn>
                </template>
              </template>
            </DatetimePicker>
          </div>
          <div>
            <v-btn @click="applyFilter" color="transparent" class="filter-icon" rounded icon small>
              <v-icon color="accentText">mdi-calendar-filter</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
      <MapBasic :markers="markers" :disable-back-navigation="openFilter"/>
    </TabBasic>
  </div>
</template>

<script>
import TabBasic from "@/components/reusable/TabBasic/TabBasic";
import MapBasic from "@/components/MapBasic/MapBasic";
import DatetimePicker from "@/components/reusable/DatetimePicker/DatetimePicker";

export default {
  name: "LocationHistory",
  components: {MapBasic, TabBasic, DatetimePicker},
  mounted() {
    this.getUser();
    this.getUserLocationHistory(true);
  },
  data() {
    return {
      locations: [],
      user: {},
      customFilter: null,
      startDate: null,
      endDate: null,
      filterMenu: false,
      startDateOnly: null,
      endDateOnly: null,
      alerts: [],
      openFilter: false,
    };
  },
  computed: {
    maxTime() {
      if (!this.endDate || !this.startDateOnly) {
        return '23:59';
      }
      if (this.sameDay(this.endDate, this.startDateOnly)) {
        return this.endDate.getHours() + ':' + this.endDate.getMinutes();
      }
      return '23:59';
    },
    minTime() {
      if (!this.startDate || !this.endDateOnly) {
        return '00:00';
      }
      if (this.sameDay(this.startDate, this.endDateOnly)) {
        return this.startDate.getHours() + ':' + this.startDate.getMinutes();
      }
      return '00:00';
    },
    showStartToday() {
      if (!this.endDate) {
        return true;
      }
      return this.dateToEpoch(this.endDate) - this.dateToEpoch(new Date()) >= 0;
    },
    showEndToday() {
      if (!this.startDate) {
        return true;
      }
      return this.dateToEpoch(new Date()) - this.dateToEpoch(this.startDate) >= 0;
    },
    showStartNow() {
      let maxDate = new Date(),
          maxTime = this.maxTime.split(':');
      maxDate.setHours(maxTime[0]);
      maxDate.setMinutes(maxTime[1]);

      return maxDate.getTime() - new Date().getTime() >= 0;
    },
    showEndNow() {
      let minDate = new Date(),
          minTime = this.minTime.split(':');
      minDate.setHours(minTime[0]);
      minDate.setMinutes(minTime[1]);

      return new Date().getTime() - minDate.getTime() >= 0;
    },
    actualBreadcrumb() {
      return [{
        text: this.user && this.user.FullName ? this.user.FullName + ' - ' + this.$t('tooltips.locationHistory') : this.$t('tooltips.locationHistory')
      }];
    },
    customFilters() {
      return [
        {
          value: 'last6Hours',
          text: this.$t('locations.lastHours', {h: 6}),
          startDate: this.getTimeBeforeHours(6),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'last12Hours',
          text: this.$t('locations.lastHours', {h: 12}),
          startDate: this.getTimeBeforeHours(12),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'lastDay',
          text: this.$t('locations.lastDay'),
          startDate: this.getTimeBeforeHours(24),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'last2Days',
          text: this.$t('locations.lastDays', {d: 2}),
          startDate: this.getTimeBeforeHours(2 * 24),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'last3Days',
          text: this.$t('locations.lastDays', {d: 3}),
          startDate: this.getTimeBeforeHours(3 * 24),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'last4Days',
          text: this.$t('locations.lastDays', {d: 4}),
          startDate: this.getTimeBeforeHours(4 * 24),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'last5Days',
          text: this.$t('locations.lastDays', {d: 5}),
          startDate: this.getTimeBeforeHours(5 * 24),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'last6Days',
          text: this.$t('locations.lastDays', {d: 6}),
          startDate: this.getTimeBeforeHours(6 * 24),
          endDate: this.getTimeBeforeHours(0),
        },
        {
          value: 'lastWeek',
          text: this.$t('locations.lastWeek'),
          startDate: this.getTimeBeforeHours(7 * 24),
          endDate: this.getTimeBeforeHours(0),

        },
      ];
    },
    markers() {
      console.log(this.locations)
      console.log(this.alerts)
      return [...this.locations, ...this.alerts];
    }
  },
  watch: {
    startDate(val) {
      if (val) {
        this.customFilter = null;
      }
    },
    endDate(val) {
      if (val) {
        this.customFilter = null;
      }
    },
  },
  methods: {
    getUserLocationHistory(init = false) {
      let locationFinished = false, alertFinished = false;
      let url = this.$store.getters.userLocations(this.$route.params.id);
      let startDate = null, endDate = null;
      let params = {};
      if (!init) {
        if (this.startDate || this.endDate) {
          startDate = this.startDate;
          endDate = this.endDate;
        } else {
          startDate = this.customFilter.startDate;
          endDate = this.customFilter.endDate;
        }
        params.startDate = startDate;
        params.endDate = endDate;
      }
      this.axios.get(url, {
        params
      }).then(({data}) => {
        this.locations = data.Result.map(location => ({
          ...location,
          lat: location.Position.Latitude,
          lng: location.Position.Longitude
        }));
        locationFinished = true;
        if (alertFinished && !this.locations.length && !this.alerts.length) {
          this.$store.dispatch('addNotification',
              {
                type: 'warning',
                message: this.$t('warnings.noLocationHistory')
              });
        }

      });
      url = this.$store.getters.userAlerts(this.$route.params.id);
      this.axios.get(url, {
        params
      }).then(({data}) => {
        this.alerts = data.Result.filter(alert => alert.Location && alert.Location.Latitude && alert.UserID === parseInt(this.$route.params.id)).map(alert => ({
          ...alert,
          lat: alert.Location.Latitude,
          lng: alert.Location.Longitude,
          OwnerID: this.$route.params.id
        }));
        if (init) {
          this.alerts = this.alerts.slice(-10);
          console.log(this.alerts)
        }
        alertFinished = true;
        if (locationFinished && !this.locations.length && !this.alerts.length) {
          this.$store.dispatch('addNotification',
              {
                type: 'warning',
                message: this.$t('warnings.noLocationHistory')
              });
        }
      });
    },
    getUser() {
      const url = this.$store.getters.organizationUser(this.$route.params.id);
      this.axios.get(url).then(({data}) => {
        this.user = data.Result;
      });
    },
    getTimeBeforeHours(h) {
      let date = new Date();
      date.setTime(date.getTime() - h * 60 * 60 * 1000);
      return date.toISOString();
    },
    resetFilter() {
      this.customFilter = null;
      this.startDate = null;
      this.endDate = null;
      this.$refs.startDate.clearHandler();
      this.$refs.endDate.clearHandler();
    },
    applyFilter() {
      this.getUserLocationHistory();
      this.filterMenu = false;
    },
    changeCustomFilter(f) {
      this.customFilter = f;
      this.startDate = null;
      this.endDate = null;
      this.$refs.startDate.clearHandler();
      this.$refs.endDate.clearHandler();
    },
    allowedStartDates(val) {
      if (!this.endDate) {
        return true;
      }
      return this.dateToEpoch(this.endDate) - this.dateToEpoch(new Date(val)) >= 0;
    },
    dateToEpoch(date) {
      const time = new Date(date);
      time.setHours(0);
      time.setMinutes(0);
      time.setSeconds(0);
      return Math.floor(time.getTime() / 1000);
    },
    allowedEndDates(val) {
      if (!this.startDate) {
        return true;
      }
      return this.dateToEpoch(new Date(val)) - this.dateToEpoch(this.startDate) >= 0;
    },
    updateStartDate(val) {
      this.startDateOnly = new Date(val);
    },
    updateEndDate(val) {
      this.endDateOnly = new Date(val);
    },
    sameDay(d1, d2) {
      return d1.getFullYear() === d2.getFullYear() &&
          d1.getMonth() === d2.getMonth() &&
          d1.getDate() === d2.getDate();
    },
    openMenu(val) {
      this.openFilter = val;

    }
  }
};
</script>

<style lang="scss" scoped>
@import "location-history";
</style>